import React, { useState } from "react";
import { SignInLogo } from "../Constant/images";
import { Eye, EyeOff } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { message } from "antd";
import { GoogleLogin } from "@react-oauth/google";
import { validator } from "../Constant/validator";
import { useUserContext } from "../ContextAPI/useUserContext";
import { getRequest, postRequest } from "../services/index";
import { jwtDecode } from "jwt-decode";
import { setPermissions } from "../redux/AuthSlice";
import { useDispatch, useSelector } from "react-redux";
import { setRoleView } from "../redux/ToggleSlice";
import { adminLinks, hrLinks } from "../Constant/sidebarlinks";

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
    <path
      fill="#FFC107"
      d="M43.611 20.083H42V20H24v8h11.303C33.654 32.659 29.227 36 24 36c-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"
    />
    <path
      fill="#FF3D00"
      d="M6.306 14.691l6.571 4.819C14.655 16.108 18.961 13 24 13c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"
    />
    <path
      fill="#4CAF50"
      d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"
    />
    <path
      fill="#1976D2"
      d="M43.611 20.083H42V20H24v8h11.303a12.04 12.04 0 0 1-4.087 5.571l.003-.002 6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"
    />
  </svg>
);

const SignIn = () => {
  const { login } = useUserContext();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  // const userRole = location.state && location.state.selectedAccount;
  const userRole = location.state?.selectedAccount || "management";
  const roleView = useSelector((state) => state.toggler.roleView);

  // useEffect(() => {
  //   if (userRole === "" || userRole === undefined || userRole === null) {
  //     return navigate("/sign-in-first");
  //   }
  // }, [navigate, userRole]);

  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  // const [rememberMe, setRememberMe] = useState(false);
  const [signInForm, setSignInForm] = useState({
    userEmail: "",
    userPassword: "",
  });

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handlerChange = (e) => {
    const { name, value } = e.target;
    setSignInForm((preState) => ({
      ...preState,
      [name]: value,
    }));
  };

  const apiRoute = "/api/Auth/login";
  const googleApiRoute = "/api/Auth/signin-google";

  // const handleCheckboxChange = (event) => {
  //   setRememberMe(event.target.checked);
  // };

  const completeLogin = async (token) => {
    const user = jwtDecode(token);

    if (user?.["userType"] !== userRole) {
      message.error("Please select correct account!");
      return false;
    }

    const managementResponse = await getRequest(`/api/Management/${user?.id}`);
    const permissions = managementResponse.data.permissions || [];
    const dummyT = (key) => key;

    let effectiveRoleView = roleView;
    if (user?.["userType"] === "management") {
      if (permissions.length === 1 && permissions[0] === "HRMS") {
        effectiveRoleView = "hr";
        dispatch(setRoleView("hr"));
      } else {
        effectiveRoleView = "admin";
        dispatch(setRoleView("admin"));
      }
    }

    const links =
      effectiveRoleView === "admin" ? adminLinks(dummyT) : hrLinks(dummyT);

    const allowedLinks = links.filter((link) =>
      link.permission ? permissions.includes(link.permission) : true
    );
    const firstAllowedPath = allowedLinks[0]?.path || "/no-access";

    const auth = {
      token,
      userId: user?.id,
      user: user?.["userType"] === "management" ? "admin" : user?.["userType"],
      permissions,
    };
    login(auth);
    dispatch(setPermissions(permissions));
    localStorage.setItem("permissions", JSON.stringify(permissions));
    navigate(firstAllowedPath);
    return true;
  };

  const handleLogin = async () => {
    const obj = {
      email: signInForm.userEmail,
      password: signInForm.userPassword,
    };
    const validate = validator(obj);
    if (validate !== "success") {
      message.error(validate);
      return;
    }

    try {
      setLoading(true);
      const { data } = await postRequest(apiRoute, obj);
      if (data?.token) {
        await completeLogin(data.token);
      } else if (data?.errors?.["non_field_errors"]) {
        message.error(data.errors["non_field_errors"][0]);
      } else {
        message.error("Something went wrong, please try again..");
      }
    } catch (err) {
      console.error("Login Error:", err);

      if (err.status === 401) {
        message.error(
          err.data?.message || "Incorrect password. Please try again."
        );
      } else if (err.status === 404) {
        message.error(
          err.data?.message || "User not found. Please check your email."
        );
      } else if (err.message) {
        message.error(err.message);
      } else {
        message.error("An unexpected error occurred. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    const idToken = credentialResponse?.credential;
    if (!idToken) {
      message.error("Google Sign-In failed. Please try again.");
      return;
    }

    try {
      setGoogleLoading(true);
      const { data } = await postRequest(googleApiRoute, { idToken });
      if (data?.token) {
        await completeLogin(data.token);
      } else {
        message.error(
          data?.message || "Google Sign-In failed. Please try again."
        );
      }
    } catch (err) {
      console.error("Google Login Error:", err);

      if (err.status === 401 || err.status === 403) {
        message.error(
          err.data?.message ||
            "Google account is not authorized. Please contact support."
        );
      } else if (err.status === 404) {
        message.error(
          err.data?.message ||
            "No account found for this Google email. Please apply first."
        );
      } else if (err.message) {
        message.error(err.message);
      } else {
        message.error("Google Sign-In failed. Please try again.");
      }
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleGoogleError = () => {
    message.error("Google Sign-In was cancelled or failed. Please try again.");
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleLogin();
    }
  };

  return (
    <>
      <div className="signin_page">
        <div className="container">
          <div className="logo_img">
            <img src={SignInLogo} alt="logo-img" width={100} />
          </div>
          <div className="row sign_in_box2 g-0">
            <div className="col-lg-5 col-md-8 col-sm-10">
              <div className="form-box bg-white rounded-4 w-100 h-100">
                <div className="pt-3 ps-4 pe-4 d-flex flex-column h-100">
                  <h3 className="poppins-bold sign_in_heading mt-3">Login</h3>
                  <p className="poppins-regular sign_in_paragraph">
                    Please enter the information below to log in to your
                    account.
                  </p>
                  <div className="pt-1">
                    <input
                      name="userEmail"
                      value={signInForm.userEmail}
                      onChange={handlerChange}
                      type="email"
                      className="sign_in_input mt-3"
                      placeholder="Enter Your Email"
                      aria-label="Email"
                    />
                  </div>
                  <div className="pt-1">
                    <div className="input-group mt-3">
                      <input
                        name="userPassword"
                        value={signInForm.userPassword}
                        onChange={handlerChange}
                        type={showPassword ? "text" : "password"}
                        className="sign_in_input password-input"
                        placeholder="Enter Password"
                        onKeyDown={handleKeyPress}
                        aria-label="Password"
                      />
                      <button
                        className="password-toggle-btn"
                        type="button"
                        onClick={togglePasswordVisibility}
                        aria-label={
                          showPassword ? "Hide password" : "Show password"
                        }
                      >
                        {showPassword ? <Eye /> : <EyeOff />}
                      </button>
                    </div>
                  </div>
                  {/* Remember Me and Forgot Password hidden per request
                  <div className="d-flex justify-content-between align-items-center mt-3">
                    <div className="form-check form-switch d-flex align-items-center gap-2">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        role="switch"
                        id="flexSwitchCheckDefault"
                        checked={rememberMe}
                        onChange={handleCheckboxChange}
                      />
                      <label className="sign-in-toggle poppins-regular">
                        Remember Me
                      </label>
                    </div>
                    <div>
                      <Link
                        className="poppins-semibold sign-in-forgot"
                        to="/forgot-password"
                      >
                        Forgot Password?
                      </Link>
                    </div>
                  </div>
                  */}
                  <div className="text-decoration-none">
                    <div className="d-grid gap-2 mt-4">
                      <button
                        className="btn sign_in_btn pt-3 pb-3 text-uppercase poppins-semibold rounded-3"
                        type="button"
                        onClick={handleLogin}
                        disabled={loading || googleLoading}
                      >
                        {loading ? "Loading ..." : "Login"}
                      </button>
                    </div>
                  </div>

                  <div className="google-auth-divider mt-3">
                    <span>OR</span>
                  </div>

                  <div className="google-sign-in-custom mt-3 mb-2">
                    <button
                      className="btn google-sign-in-btn w-100"
                      type="button"
                      disabled={googleLoading}
                      aria-label="Sign in with Google"
                      tabIndex={0}
                    >
                      <GoogleIcon />
                      {googleLoading
                        ? "Signing in with Google..."
                        : "Sign in with Google"}
                    </button>
                    {!googleLoading && (
                      <div className="google-login-overlay" aria-hidden="true">
                        <GoogleLogin
                          onSuccess={handleGoogleSuccess}
                          onError={handleGoogleError}
                          theme="outline"
                          size="large"
                          text="signin_with"
                          shape="rectangular"
                          width="400"
                          logo_alignment="left"
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SignIn;
